import { useMemo } from 'react';

import {
  Building2,
  ChartSpline,
  LogOut,
  Sparkles,
  UserRoundPen,
  UsersRound
} from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/shadcn/dropdown-menu';

import { env } from '@/constant/env';
import { Button } from '@/shadcn/button';
import { parseEntities } from '@/components/userDashboard/utils';
import { logout } from '@/client/account';
import { useHistory } from 'react-router';
import { ROUTE_LOGIN } from '@/constant/routes';
import { useUser } from '@/hooks/useUser';
import { AvatarComponent } from '@/components/ui/avatar/avatar';

export const NavUser = () => {
  const { user, loading } = useUser();
  const history = useHistory();

  const { organization } = useMemo(
    () => parseEntities(user?.memberOf.nodes || []),
    [user?.memberOf.nodes]
  );

  const onSignOut = async () => {
    await logout();
    history.push(ROUTE_LOGIN);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={loading}>
        <Button
          variant="ghost"
          size="icon"
          className="size-9 rounded-full p-0"
          disabled={loading}
        >
          <AvatarComponent size={36} source={user?.avatar} name={user?.name} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
        side="bottom"
        align="end"
        sideOffset={4}
      >
        {user && (
          <>
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <AvatarComponent
                  size={32}
                  source={user.avatar}
                  name={user.name}
                />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="text-muted-foreground truncate text-xs">
                    {user.email}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuGroup>
          <DropdownMenuItem>
            <a
              href={env.VITE_ACCOUNT_URL}
              target="_blank"
              rel="noopener noreferrer"
              draggable="false"
              className="text-foreground flex w-full items-center gap-2"
            >
              <UserRoundPen /> My profile
            </a>
          </DropdownMenuItem>
          {organization?.rowId && (
            <>
              <DropdownMenuItem>
                <a
                  href={`${env.VITE_ACCOUNT_URL}/organization/${organization?.rowId}/members`}
                  target="_blank"
                  rel="noopener noreferrer"
                  draggable="false"
                  className="text-foreground flex w-full items-center gap-2"
                >
                  <Building2 /> Organization
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <a
                  href={`${env.VITE_ACCOUNT_URL}/organization/${organization?.rowId}/teams`}
                  target="_blank"
                  rel="noopener noreferrer"
                  draggable="false"
                  className="text-foreground flex w-full items-center gap-2"
                >
                  <UsersRound /> Teams
                </a>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem>
            <a
              href={env.VITE_ANALYTICS_URL}
              target="_blank"
              rel="noopener noreferrer"
              draggable="false"
              className="text-foreground flex w-full items-center gap-2"
            >
              <ChartSpline /> Analytics
            </a>
          </DropdownMenuItem>

          {user?.isSuperuser && (
            <DropdownMenuItem>
              <a
                href={env.VITE_STUDIO_URL}
                target="_blank"
                rel="noopener noreferrer"
                draggable="false"
                className="text-foreground flex w-full items-center gap-2"
              >
                <Sparkles /> Studio
              </a>
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onSignOut()} variant="destructive">
          <LogOut />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
